import { BaseStyles, ThemeProvider } from "@primer/react";
import { useEffect } from "react";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { getAccessToken } from "./api/apiClient";
import { appConst } from "./AppConst";
import { HubProvider } from "./contexts/HubContext";
import AppRoutes from "./content/routers";
import { rootActions } from "./state/actions/rootActions";
import { store } from "./state/store";
import { customTheme } from "./theme/customTheme";

/**
 * Set favicon + document.title từ AppConst branding.
 * Runtime để dùng cùng fallback default trong AppConst (không phụ thuộc env build-time).
 */
const FaviconSetter = () => {
    useEffect(() => {
        const { logoUrl, webName, tagline } = appConst.branding;
        if (logoUrl) {
            let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = logoUrl;
        }
        document.title = `${tagline} — ${webName}`;
    }, []);
    return null;
};

const SessionRestorer = () => {
    useEffect(() => {
        // Khôi phục session từ localStorage khi app mount (F5).
        const token = getAccessToken();
        const userRaw = localStorage.getItem("user_info");
        const expiresAt = localStorage.getItem("expires_at");
        if (token && userRaw) {
            try {
                const user = JSON.parse(userRaw);
                if (expiresAt && new Date(expiresAt) < new Date()) {
                    localStorage.removeItem("access_token");
                    localStorage.removeItem("user_info");
                    localStorage.removeItem("expires_at");
                    return;
                }
                store.dispatch(
                    rootActions.auth.RESTORE_SESSION({
                        access_token: token,
                        expires_at: expiresAt || "",
                        user,
                        // Giữ cờ buộc đổi mật khẩu qua F5. Server vẫn chặn cứng bằng claim pwd_must_change.
                        must_change_password: localStorage.getItem("must_change_password") === "1",
                        password_weak_reason: localStorage.getItem("password_weak_reason") || undefined,
                    })
                );
            } catch {
                /* ignore */
            }
        }
    }, []);
    return null;
};

const App = () => {
    return (
        <Provider store={store}>
            <ThemeProvider theme={customTheme} colorMode="day">
                <BaseStyles>
                    <BrowserRouter>
                        <FaviconSetter />
                        <SessionRestorer />
                        <HubProvider>
                            <AppRoutes />
                        </HubProvider>
                        <Toaster
                            position="top-right"
                            toastOptions={{
                                success: { style: { background: "#1B7F36", color: "#fff" } },
                                error: { style: { background: "#A30F26", color: "#fff" } },
                            }}
                        />
                    </BrowserRouter>
                </BaseStyles>
            </ThemeProvider>
        </Provider>
    );
};

export default App;
